import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { FileText, Image, Video, Users, Calendar, Trophy, Zap, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    banners: 0,
    news: 0,
    photos: 0,
    videos: 0,
    participants: 0,
    matches: 0,
    events: 0,
  });
  const [recentMatches, setRecentMatches] = useState([]);
  const [recentNews, setRecentNews] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [dashboardAnalytics, setDashboardAnalytics] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedSeries, setSelectedSeries] = useState('all');
  const [loading, setLoading] = useState(true);
  const { user, isClubAdmin } = useAuth();

  useEffect(() => {
    loadStats();
  }, []);

  const loadAnalytics = async (eventId = '') => {
    const params = eventId ? { event_id: eventId } : {};
    const analyticsRes = await apiService.admin.getDashboardAnalytics(params);
    const payload = analyticsRes.data || null;
    setDashboardAnalytics(payload);
    if (payload?.selected_event_id) {
      setSelectedEventId(payload.selected_event_id);
    }
  };

  const loadStats = async () => {
    try {
      if (isClubAdmin) {
        const participants = await apiService.admin.getParticipants();
        const ownTeam = participants.data?.[0];

        const matchesRes = await apiService.getMatches();
        const ownMatches = (matchesRes.data || []).filter(
          (match) => match.home_team === ownTeam?.name || match.away_team === ownTeam?.name
        );

        setStats({
          banners: 0,
          news: 0,
          photos: 0,
          videos: 0,
          participants: ownTeam ? 1 : 0,
          matches: ownMatches.length,
          events: 0,
        });

        setRecentMatches(ownMatches.slice(0, 5));
        return;
      }

      const [banners, news, photos, videos, participants, matches, events] = await Promise.all([
        apiService.getBanners(),
        apiService.getNews(),
        apiService.getPhotos(),
        apiService.getVideos(),
        apiService.getParticipants(),
        apiService.getMatches(),
        apiService.admin.getEvents(),
      ]);

      setStats({
        banners: banners.data.length,
        news: news.data.length,
        photos: photos.data.length,
        videos: videos.data.length,
        participants: participants.data.length,
        matches: matches.data.length,
        events: events.data.length,
      });

      const sortedMatches = [...(matches.data || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
      const sortedNews = [...(news.data || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
      const sortedEvents = [...(events.data || [])].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

      setRecentMatches(sortedMatches.slice(0, 5));
      setRecentNews(sortedNews.slice(0, 4));
      setRecentEvents(sortedEvents.slice(0, 4));

      await loadAnalytics();
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventAnalyticsChange = async (e) => {
    const nextEventId = e.target.value;
    setSelectedEventId(nextEventId);
    try {
      await loadAnalytics(nextEventId);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const parseYear = (eventLike = {}) => {
    if (eventLike.start_date) {
      const d = new Date(eventLike.start_date);
      if (!Number.isNaN(d.getTime())) return String(d.getFullYear());
    }
    const name = eventLike.name || eventLike.event_name || '';
    const yearMatch = name.match(/(20\d{2})/);
    return yearMatch ? yearMatch[1] : 'Unknown';
  };

  const parseSeries = (eventLike = {}) => {
    const name = (eventLike.name || eventLike.event_name || '').toLowerCase();
    const m = name.match(/seri\s*([0-9]+)/i);
    return m ? `Seri ${m[1]}` : 'Lainnya';
  };

  const exportCsv = (filename, headers, rows) => {
    const escapeCell = (value) => {
      const text = String(value ?? '');
      if (text.includes(',') || text.includes('"') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };
    const csv = [headers.map(escapeCell).join(',')]
      .concat(rows.map((row) => row.map(escapeCell).join(',')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const historicalRaw = dashboardAnalytics?.historical || [];
  const yearOptions = ['all', ...Array.from(new Set(historicalRaw.map((e) => parseYear(e))))];
  const seriesOptions = ['all', ...Array.from(new Set(historicalRaw.map((e) => parseSeries(e))))];

  const historicalFiltered = historicalRaw.filter((row) => {
    const yearOk = selectedYear === 'all' || parseYear(row) === selectedYear;
    const seriesOk = selectedSeries === 'all' || parseSeries(row) === selectedSeries;
    return yearOk && seriesOk;
  });

  const eventsFiltered = (dashboardAnalytics?.events || []).filter((evt) => {
    const yearOk = selectedYear === 'all' || parseYear(evt) === selectedYear;
    const seriesOk = selectedSeries === 'all' || parseSeries(evt) === selectedSeries;
    return yearOk && seriesOk;
  });

  const selectedRegional = dashboardAnalytics?.selected?.regional || {};

  useEffect(() => {
    if (isClubAdmin) return;
    if (!eventsFiltered.length) return;
    const exists = eventsFiltered.some((evt) => evt.id === selectedEventId);
    if (!exists) {
      const nextId = eventsFiltered[0].id;
      setSelectedEventId(nextId);
      loadAnalytics(nextId).catch((error) => {
        console.error('Error syncing event filter:', error);
      });
    }
  }, [isClubAdmin, selectedEventId, selectedYear, selectedSeries, eventsFiltered]);

  const handleExportOnline = () => {
    const rows = (dashboardAnalytics?.selected?.online_by_category || []).map((r) => [r.category, r.teams, r.athletes, r.entities]);
    exportCsv('registrasi-online-per-kelompok-umur.csv', ['Kelompok Umur', 'Tim', 'Atlet', 'Sekolah/Klub'], rows);
  };

  const handleExportParticipating = () => {
    const rows = (dashboardAnalytics?.selected?.participating_by_category || []).map((r) => [r.category, r.teams, r.athletes, r.entities]);
    exportCsv('register-ulang-partisipasi-per-kelompok-umur.csv', ['Kelompok Umur', 'Tim', 'Atlet', 'Sekolah/Klub'], rows);
  };

  const handleExportHistorical = () => {
    const rows = historicalFiltered.map((r) => {
      const delta = r.delta_vs_previous?.online_teams || 0;
      const base = (r.online_totals?.teams || 0) - delta;
      const pct = base > 0 ? ((delta / base) * 100) : (delta > 0 ? 100 : 0);
      return [
        r.event_name,
        r.start_date ? new Date(r.start_date).toLocaleDateString('id-ID') : '-',
        r.online_totals?.teams || 0,
        r.online_totals?.athletes || 0,
        r.participating_totals?.teams || 0,
        r.participating_totals?.athletes || 0,
        delta,
        `${pct.toFixed(1)}%`,
      ];
    });
    exportCsv('histori-event-perbandingan.csv', ['Event', 'Tanggal', 'Tim Online', 'Atlet Online', 'Tim Partisipasi', 'Atlet Partisipasi', 'Delta Tim', 'Delta %'], rows);
  };

  const handleExportRegional = () => {
    const rows = [];
    (selectedRegional.province || []).forEach((r) => rows.push(['Provinsi', r.name, r.teams, r.entities]));
    (selectedRegional.city || []).forEach((r) => rows.push(['Kabupaten/Kota', r.name, r.teams, r.entities]));
    (selectedRegional.district || []).forEach((r) => rows.push(['Kecamatan', r.name, r.teams, r.entities]));
    exportCsv('rekap-regional-partisipasi.csv', ['Level', 'Wilayah', 'Tim', 'Sekolah/Klub'], rows);
  };

  const renderCategoryTable = (title, rows = []) => (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 sm:p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-sm">Belum ada data.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-900">Kelompok Umur</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-900">Tim</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-900">Atlet</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-900">Sekolah/Klub</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.category} className="border-b">
                  <td className="px-3 py-2 font-medium text-gray-800">{row.category}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{row.teams}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{row.athletes}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{row.entities}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderRegionTable = (title, rows = []) => (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 sm:p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-sm">Belum ada data partisipasi regional.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-900">Wilayah</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-900">Tim</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-900">Sekolah/Klub</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name} className="border-b">
                  <td className="px-3 py-2 text-gray-800">{row.name}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{row.teams}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{row.entities}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const statCards = [
    ...(isClubAdmin
      ? [
          { title: 'Tim Saya', value: stats.participants, icon: Users, color: 'from-amber-500 to-orange-500' },
          { title: 'Jadwal/Pertandingan Tim', value: stats.matches, icon: Calendar, color: 'from-indigo-500 to-blue-500' },
        ]
      : [
          { title: 'Total Event', value: stats.events, icon: Zap, color: 'from-sky-500 to-cyan-500' },
          { title: 'Total Tim', value: stats.participants, icon: Users, color: 'from-amber-500 to-orange-500' },
          { title: 'Total Pertandingan', value: stats.matches, icon: Calendar, color: 'from-indigo-500 to-blue-500' },
          { title: 'Total Pemain Ikut', value: dashboardAnalytics?.total_players_joined || 0, icon: Trophy, color: 'from-fuchsia-500 to-pink-500' },
          { title: 'Total Berita', value: stats.news, icon: FileText, color: 'from-emerald-500 to-green-500' },
          { title: 'Total Foto', value: stats.photos, icon: Image, color: 'from-purple-500 to-violet-500' },
          { title: 'Total Video', value: stats.videos, icon: Video, color: 'from-rose-500 to-red-500' },
        ]),
  ];

  const quickActions = isClubAdmin
    ? [
        { label: 'Kelola Tim Saya', path: '/admin/participants', icon: Users, tone: 'border-amber-200 bg-amber-50 text-amber-700' },
        { label: 'Lihat Pertandingan', path: '/admin/matches', icon: Calendar, tone: 'border-blue-200 bg-blue-50 text-blue-700' },
        { label: 'Pengaturan Akun', path: '/admin/settings', icon: Trophy, tone: 'border-slate-200 bg-slate-50 text-slate-700' },
      ]
    : [
        { label: 'Kelola Event', path: '/admin/events', icon: Zap, tone: 'border-cyan-200 bg-cyan-50 text-cyan-700' },
        { label: 'Tambah Berita', path: '/admin/news', icon: FileText, tone: 'border-green-200 bg-green-50 text-green-700' },
        { label: 'Tambah Pertandingan', path: '/admin/matches', icon: Calendar, tone: 'border-blue-200 bg-blue-50 text-blue-700' },
        { label: 'Upload Galeri', path: '/admin/photos', icon: Image, tone: 'border-purple-200 bg-purple-50 text-purple-700' },
      ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-600">Memuat...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="rounded-2xl p-6 sm:p-8 bg-gradient-to-r from-gray-900 via-gray-800 to-emerald-900 text-white shadow-lg">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="text-emerald-300 text-sm font-semibold tracking-wide">RINGKASAN ADMIN</p>
              <h2 className="text-2xl sm:text-3xl font-bold mt-1">Selamat datang, {user?.username}</h2>
              <p className="text-white/80 mt-2 text-sm sm:text-base">
                {isClubAdmin
                  ? `Kelola performa klub ${user?.participant_name || ''} dengan data terbaru.`
                  : 'Pantau registrasi, partisipasi, histori event, dan persebaran regional dari satu dashboard.'}
              </p>
            </div>
            <div className="rounded-xl bg-white/10 border border-white/20 px-4 py-3 w-fit">
              <p className="text-xs uppercase tracking-widest text-white/70">Tanggal</p>
              <p className="text-sm font-semibold">{new Date().toLocaleDateString('id-ID', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.title} className="bg-white rounded-xl shadow-md border border-gray-100 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">{stat.title}</p>
                    <p className="text-3xl font-extrabold text-gray-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} rounded-xl p-3.5 shadow`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!isClubAdmin && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Analisis Registrasi Event</h3>
                <p className="text-sm text-gray-500">Pilih event untuk melihat ringkasan registrasi online dan tim yang ikut berpartisipasi.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm min-w-[140px]"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y === 'all' ? 'Semua Tahun' : y}</option>
                  ))}
                </select>
                <select
                  value={selectedSeries}
                  onChange={(e) => setSelectedSeries(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm min-w-[140px]"
                >
                  {seriesOptions.map((s) => (
                    <option key={s} value={s}>{s === 'all' ? 'Semua Seri' : s}</option>
                  ))}
                </select>
                <select
                  value={selectedEventId}
                  onChange={handleEventAnalyticsChange}
                  className="border rounded-lg px-3 py-2 text-sm min-w-[260px]"
                >
                  {eventsFiltered.map((evt) => (
                    <option key={evt.id} value={evt.id}>{evt.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {!isClubAdmin && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div className="space-y-2">
              <div className="flex justify-end">
                <button onClick={handleExportOnline} className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-700">Export Excel (CSV)</button>
              </div>
              {renderCategoryTable('Informasi Registrasi Online per Kelompok Umur', dashboardAnalytics?.selected?.online_by_category || [])}
            </div>
            <div className="space-y-2">
              <div className="flex justify-end">
                <button onClick={handleExportParticipating} className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-700">Export Excel (CSV)</button>
              </div>
              {renderCategoryTable('Informasi Register Ulang / Partisipasi per Kelompok Umur', dashboardAnalytics?.selected?.participating_by_category || [])}
            </div>
          </div>
        )}

        {!isClubAdmin && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Histori Event dan Perbandingan</h3>
              <button onClick={handleExportHistorical} className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-700">Export Excel (CSV)</button>
            </div>
            {!historicalFiltered.length ? (
              <p className="text-gray-500 text-sm">Belum ada histori event.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-3 py-2 font-semibold text-gray-900">Event</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-900">Tim Online</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-900">Atlet Online</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-900">Tim Partisipasi</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-900">Atlet Partisipasi</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-900">Selisih Tim</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-900">Selisih %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicalFiltered.map((row) => {
                      const delta = row.delta_vs_previous?.online_teams || 0;
                      const base = (row.online_totals?.teams || 0) - delta;
                      const pct = base > 0 ? ((delta / base) * 100) : (delta > 0 ? 100 : 0);
                      const pctText = `${delta > 0 ? '+' : ''}${pct.toFixed(1)}%`;
                      return (
                      <tr key={row.event_id} className="border-b">
                        <td className="px-3 py-2 text-gray-800">
                          <div className="font-medium">{row.event_name}</div>
                          <div className="text-xs text-gray-500">{row.start_date ? new Date(row.start_date).toLocaleDateString('id-ID') : '-'}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">{row.online_totals?.teams || 0}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{row.online_totals?.athletes || 0}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{row.participating_totals?.teams || 0}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{row.participating_totals?.athletes || 0}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : 'text-gray-700'}`}>{delta > 0 ? '+' : ''}{delta}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : 'text-gray-700'}`}>{pctText}</td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!isClubAdmin && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className="xl:col-span-3 flex justify-end">
              <button onClick={handleExportRegional} className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-700">Export Excel (CSV)</button>
            </div>
            {renderRegionTable('Rekap Regional Provinsi', selectedRegional?.province || [])}
            {renderRegionTable('Rekap Regional Kabupaten/Kota', selectedRegional?.city || [])}
            {renderRegionTable('Rekap Regional Kecamatan', selectedRegional?.district || [])}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <div className="xl:col-span-2 bg-white rounded-xl shadow-md border border-gray-100 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Pertandingan Terbaru</h3>
              <Link to="/admin/matches" className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
                Lihat semua <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            {recentMatches.length === 0 ? (
              <p className="text-gray-500 text-sm">Belum ada pertandingan.</p>
            ) : (
              <div className="space-y-3">
                {recentMatches.map((match) => (
                  <div key={match.id} className="rounded-lg border border-gray-100 p-3.5 bg-gray-50 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">{match.home_team} vs {match.away_team}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{match.category} • {new Date(match.date).toLocaleDateString('id-ID')}</p>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${match.status === 'finished' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {match.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 sm:p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Aksi Cepat</h3>
            <div className="space-y-3">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link key={action.path} to={action.path} className={`block rounded-lg border p-3 transition hover:shadow-sm ${action.tone}`}>
                    <div className="flex items-center gap-2.5">
                      <Icon className="h-4.5 w-4.5" />
                      <span className="text-sm font-semibold">{action.label}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {!isClubAdmin && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Event Terbaru</h3>
                <Link to="/admin/events" className="text-sm font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center gap-1">
                  Kelola <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              {recentEvents.length === 0 ? (
                <p className="text-gray-500 text-sm">Belum ada event.</p>
              ) : (
                <div className="space-y-3">
                  {recentEvents.map((event) => (
                    <div key={event.id} className="rounded-lg border border-gray-100 p-3.5 bg-gray-50">
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">{event.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{event.format} • {event.location || '-'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Berita Terbaru</h3>
                <Link to="/admin/news" className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
                  Kelola <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              {recentNews.length === 0 ? (
                <p className="text-gray-500 text-sm">Belum ada berita.</p>
              ) : (
                <div className="space-y-3">
                  {recentNews.map((newsItem) => (
                    <div key={newsItem.id} className="rounded-lg border border-gray-100 p-3.5 bg-gray-50">
                      <p className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2">{newsItem.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{new Date(newsItem.date).toLocaleDateString('id-ID')}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
